<template>
  <div>
    <div class="flex flex-col md:flex-row md:gap-x-8 lg:gap-x-16">
      <div class="w-full md:w-1/2">
        <!-- Active toggle & Name block starts here -->
        <div>
          <fwb-toggle v-model="isActive" label="Active" />
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Set your phone numbers to inactive to have them drop any incoming calls immediately.
          </p>

          <div class="mt-4">
            <fwb-input
              v-model="friendlyName"
              label="Name"
              placeholder="Enter the name for this phone number">
              <template #helper>
                You can assign unique names to phone numbers to remind you what they are used for.
              </template>
            </fwb-input>
          </div>

          <div class="mt-4">
            <fwb-select
              v-model="legLength"
              :options="legLengthOptions"
              label="Leg Length">
              <template #helper>
                Choose how long you want a Jensen phone call leg to last before going to voicemail or the next number.
              </template>
            </fwb-select>
          </div>

          <label class="block mt-4 text-sm font-medium text-gray-900 dark:text-white">
            Caller ID
          </label>

          <ul
            class="mt-2 items-start w-full text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-lg sm:flex dark:bg-gray-700 dark:border-gray-600 dark:text-white">
            <li class="!m-0 pl-3 flex border-gray-200 rounded-t-lg dark:border-gray-600">
              <fwb-radio
                v-model="callerIdMode"
                label="Use Caller as ID"
                name="caller_id"
                value="use_caller_as_id" />
            </li>
            <li class="!m-0 pl-3 flex border-gray-200 rounded-t-lg dark:border-gray-600">
              <fwb-radio
                v-model="callerIdMode"
                label="Use Call Tracking Number as ID"
                name="caller_id"
                value="use_call_tracking_number_as_id" />
            </li>
          </ul>
          <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
            This is the number that the call will show as coming from when connected to an agent.
          </p>

          <!-- Roboblock and Spam filter -->
          <div class="mt-6">
            <fwb-toggle v-model="roboblockEnabled" label="Enable RoboBlock" />
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Require the caller to press a button before the call is connected to the agent.
            </p>
          </div>

          <div class="mt-6">
            <fwb-toggle v-model="blocklistEnabled" label="Enable Blocklist" />
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Callers from <a href="/blocklist/edit" class="text-blue-400 hover:underline">blocklisted numbers</a> will not be connected to an agent.
            </p>
          </div>
          <!-- Roboblock and Spam filter ends here -->

          <hr class="h-px my-8 bg-gray-200 border-0 dark:bg-gray-700">
        </div>
        <!-- Active & Name block ends here -->

        <!-- Call Flow block starts here -->
        <div>
          <h5 class="text-lg font-bold dark:text-white">
            Call Flow
          </h5>
          <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Call flows determine what a number does when called.
          </p>

          <ul
            class="mt-4 items-start w-full text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-lg sm:flex dark:bg-gray-700 dark:border-gray-600 dark:text-white">
            <li class="!m-0 pl-3 flex border-gray-200 rounded-t-lg dark:border-gray-600">
              <fwb-radio
                v-model="callFlowType"
                label="Ring Through"
                name="call_flow_type"
                value="ring_through" />
            </li>
          </ul>
          <p v-if="callFlowType === 'ring_through'"
            class="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Ring Through is 1-1 relationship to send calls directly through to a single number.
          </p>

          <div class="mt-6">
            <fwb-toggle v-model="challengeEnabled" label="Enable Challenge" />
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              A "challenge" forces the answering agent to press a button before being connected to the caller.
            </p>
          </div>

          <div class="mt-4">
            <twilio-phone-input
              v-model="ringThroughNumber"
              @input="cleanError('ringThroughNumber')"
              @update-errors="updateErrors"
              label="Number To Dial"
              helper="Enter the answering agent's phone number in the format +15095550100."
              :error-text="errorText('ringThroughNumber')" />
          </div>
        </div>
        <!-- Call Flow block ends here -->
      </div>

      <div class="w-full md:w-1/2">
        <!-- Business hours block starts here -->
        <div>
          <h5 class="text-lg font-bold dark:text-white">
            Business Hours
          </h5>
          <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Business hours can be set on a daily basis. When a call comes in outside of business hours it goes directly
            to
            voicemail.
            0-23:59 is equivalent to the number always being on.
          </p>

          <div class="mt-4">
            <multi-select
              v-model="selectedTimeZone"
              :options="timeZones"
              track-by="id"
              label="label"
              placeholder="Time Zone"
              class="min-w-max placeholder-gray-300 text-sm mt-2 bg-gray-50"
              input-container-class="bg-gray-50"
              :allow-empty="false" />

            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Set the Time Zone of the business operating hours this phone number is assigned to.
            </p>
          </div>

          <div class="mt-4">
            <week-schedule
              v-model="scheduleSettings"
              :name="'Business Hours'"
              :errors="{}" />
          </div>

          <hr class="h-px my-8 bg-gray-200 border-0 dark:bg-gray-700">
        </div>
        <!-- Business hours block ends here -->

        <!-- Voice Mail block starts here -->
        <div>
          <h5 class="text-lg font-bold dark:text-white">
            Voicemail
          </h5>
          <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Your voicemail option determines what a caller hears when the phone isn't picked up or the call is made
            outside of operating hours.
          </p>

          <div class="mt-4">
            <fwb-toggle v-model="directToVm" label="Direct To VM" />
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Send calls directly to voicemail without ringing the agent.
            </p>
          </div>

          <label class="block mt-6 text-sm font-medium text-gray-900 dark:text-white">
            VM Greeting
          </label>

          <ul
            class="mt-2 items-start w-full text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-lg sm:flex dark:bg-gray-700 dark:border-gray-600 dark:text-white">
            <li class="!m-0 pl-3 flex border-gray-200 rounded-t-lg dark:border-gray-600">
              <fwb-radio
                v-model="voiceMailMessageType"
                @change="cleanError('voiceMailText')"
                label="Text to Speech"
                name="voice_mail_message_type"
                value="text" />
            </li>
            <li class="!m-0 pl-3 flex border-gray-200 rounded-t-lg dark:border-gray-600">
              <fwb-radio
                v-model="voiceMailMessageType"
                @change="cleanError('voiceMailUrl')"
                label="MP3 URL"
                name="voice_mail_message_type"
                value="mp3" />
            </li>
          </ul>

          <div class="mt-4"
            v-if="voiceMailMessageType === 'text'">
            <fwb-input
              v-model="voiceMailText"
              label="Text to Speech"
              placeholder="Enter text for message greeting"
              @input="cleanError('voiceMailText')"
              :validation-status="hasError('voiceMailText')">
              <template v-if="!hasError('voiceMailText')" #helper>
                The text you input here will be spoken by a text to speech robot when the caller reaches your voicemail.
              </template>

              <template v-if="hasError('voiceMailText')" #validationMessage>
                {{ errorText('voiceMailText') }}
              </template>
            </fwb-input>
          </div>

          <div class="mt-4" v-if="voiceMailMessageType === 'mp3'">
            <fwb-input
              v-model="voiceMailUrl"
              label="MP3 URL"
              placeholder="Enter URL of MP3 file"
              @input="cleanError('voiceMailUrl')"
              :validation-status="hasError('voiceMailUrl')">
              <template v-if="!hasError('voiceMailUrl')" #helper>
                Enter a MP3 (https://yoursite.com/message.mp3) URL to be played as a greeting when callers reach your
                voicemail.
              </template>

              <template v-if="hasError('voiceMailUrl')" #validationMessage>
                {{ errorText('voiceMailUrl') }}
              </template>
            </fwb-input>
          </div>

          <div class="mt-4">
            <fwb-toggle v-model="vmTranscription" label="VM Transcription" />
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {{ humanize(provider) }} offers programmatic transcriptions of voicemails that Jensen can email or text to your clients.
              <br>
              {{ humanize(provider) }} charges an extra fee for transcription services. Please check the latest {{ humanize(provider) }} prices.
            </p>
          </div>

          <div class="mt-4">
            <fwb-toggle v-model="sendNotification" label="Notification" />
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Send notification if call was directed to voicemail.
            </p>
          </div>

          <hr class="h-px my-8 bg-gray-200 border-0 dark:bg-gray-700">
        </div>
        <!-- Voice Mail block ends here -->

        <!-- Recording block starts here -->
        <div>
          <h5 class="mt-8 text-lg font-bold dark:text-white">
            Recording
          </h5>

          <div class="mt-4">
            <fwb-toggle
              v-model="recordingEnabled"
              @change="cleanError('recordingWarningText', 'recordingWarningUrl')"
              label="Enable Recording" />
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Recorded calls allow you to listen to conversations after they've taken place. Recording calls without the
              consent of the caller may not be legal in your geography. Please check local laws before recording
              callers.
            </p>
          </div>

          <div v-if="recordingEnabled">
            <label class="block mt-4 text-sm font-medium text-gray-900 dark:text-white">
              Recording Warning
            </label>

            <ul
              class="mt-2 items-start w-full text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-lg sm:flex dark:bg-gray-700 dark:border-gray-600 dark:text-white">
              <li class="!m-0 pl-3 flex border-gray-200 rounded-t-lg dark:border-gray-600">
                <fwb-radio
                  v-model="recordingWarningType"
                  @change="cleanError('recordingWarningText')"
                  label="Default Message"
                  name="recording_warning_type"
                  value="default" />
              </li>
              <li class="!m-0 pl-3 flex border-gray-200 rounded-t-lg dark:border-gray-600">
                <fwb-radio
                  v-model="recordingWarningType"
                  @change="cleanError('recordingWarningText')"
                  label="Text to Speech"
                  name="recording_warning_type"
                  value="text" />
              </li>
              <li class="!m-0 pl-3 flex border-gray-200 rounded-t-lg dark:border-gray-600">
                <fwb-radio
                  v-model="recordingWarningType"
                  @change="cleanError('recordingWarningUrl')"
                  label="MP3 URL"
                  name="recording_warning_type"
                  value="mp3" />
              </li>
            </ul>
            <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Choose what a caller hears when recording is enabled for this number.
            </p>

            <div class="mt-4"
              v-if="recordingWarningType === 'text' || recordingWarningType === 'default'">
              <fwb-input
                v-model="recordingWarningText"
                label="Recording Notice"
                :disabled="recordingWarningType === 'default'"
                placeholder="Enter text recording notice"
                @input="cleanError('recordingWarningText')"
                :validation-status="hasError('recordingWarningText')">
                <template v-if="!hasError('recordingWarningText')" #helper>
                  The text you input here will be spoken by a text to speech robot when the caller initially connects.
                </template>

                <template v-if="hasError('recordingWarningText')" #validationMessage>
                  {{ errorText('recordingWarningText') }}
                </template>
              </fwb-input>
            </div>

            <div class="mt-4" v-if="recordingWarningType === 'mp3'">
              <fwb-input
                v-model="recordingWarningUrl"
                label="Recording Notice MP3 URL"
                placeholder="Enter URL of MP3 file"
                @input="cleanError('recordingWarningUrl')"
                :validation-status="hasError('recordingWarningUrl')">
                <template v-if="!hasError('recordingWarningUrl')" #helper>
                  Enter a MP3 (https://yoursite.com/message.mp3) URL to be played as a recording notice when callers
                  connect.
                </template>

                <template v-if="hasError('recordingWarningUrl')" #validationMessage>
                  {{ errorText('recordingWarningUrl') }}
                </template>
              </fwb-input>
            </div>
          </div>
        </div>
        <!-- Recording block ends here -->
      </div>
    </div>

    <div class="actions mt-10 border-t border-gray-200 pt-5">
      <submit-spinner
        title="Update Settings"
        :form-submitting="formSubmitting"
        @click="updateSettings" />

      <a
        href="/phone_numbers"
        class="text-gray-900 bg-white border border-gray-300 focus:outline-none hover:bg-gray-100 focus:ring-4 focus:ring-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-600 dark:focus:ring-gray-700">
        Cancel
      </a>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import camelcaseKeys from 'camelcase-keys-deep'
import snakecaseKeys from 'snakecase-keys'
import { FwbToggle, FwbInput, FwbRadio, FwbSelect } from 'flowbite-vue'
import MultiSelect from 'vue-multiselect'
import WeekSchedule from '~/javascript/components/common/week_schedule.vue'
import SubmitSpinner from '~/javascript/components/common/submit_spinner.vue'
import TwilioPhoneInput from '~/javascript/components/common/twilio_phone_input.vue'
import { toastError, toastSuccess } from '~/javascript/utils/toast'
import securedAxios from '~/javascript/utils/axios'
import useValidation from '~/javascript/components/composables/validation'
import { humanize } from '~/javascript/utils/strings'

const props = defineProps({
  defaultRecordingWarning: {
    type: String,
    required: true
  },
  defaultVoiceMailMessage: {
    type: String,
    required: true
  },
  phoneNumber: {
    type: Object,
    required: true
  },
  timeZones: {
    type: Array,
    required: true,
    default: () => []
  }
})

// Main block with "active" toggle, name, leg length, caller ID
const isActive = ref(props.phoneNumber.call_flow_active)
const friendlyName = ref(props.phoneNumber.friendly_name)
const legLength = ref(props.phoneNumber.leg_length)
const legLengthOptions = [10, 20, 30, 60, 90, 180].map((value) => ({ value, name: `${value} Seconds` }))
const callerIdMode = ref(props.phoneNumber.caller_id_mode)
const roboblockEnabled = ref(props.phoneNumber.roboblock_enabled)
const blocklistEnabled = ref(props.phoneNumber.blocklist_enabled)
const provider = ref(props.phoneNumber.provider)

// Block with call flow settings
const callFlowType = ref(props.phoneNumber.call_flow_type)
const challengeEnabled = ref(props.phoneNumber.challenge_enabled)
const ringThroughNumber = ref(props.phoneNumber.ring_through_number)

// Business hours block
const tzId = props.phoneNumber.time_zone || 'Pacific Time (US & Canada)'
const selectedTimeZone = ref(props.timeZones.find((tz) => tz.id === tzId))
const scheduleSettings = ref(camelcaseKeys(props.phoneNumber.schedule, { deep: true }))

// VoiceMail settings block
const voiceMailMessageType = ref(props.phoneNumber.voice_mail_message_type)
const voiceMailText = ref(props.phoneNumber.voice_mail_text || props.defaultVoiceMailMessage)
const voiceMailUrl = ref(props.phoneNumber.voice_mail_url)
const directToVm = ref(props.phoneNumber.direct_to_vm)
const sendNotification = ref(props.phoneNumber.send_notification)
const vmTranscription = ref(props.phoneNumber.vm_transcription)

// Recording settings block
const recordingEnabled = ref(props.phoneNumber.recording_enabled)
const recordingWarningType = ref(props.phoneNumber.recording_warning_type)
const recordingWarningText = ref(props.phoneNumber.recording_warning_text || props.defaultRecordingWarning)
const recordingWarningUrl = ref(props.phoneNumber.recording_warning_url)

// Validation errors from backend and form submitting state to show spinner
const { updateErrors, hasError, errorText, cleanError } = useValidation()
const formSubmitting = ref(false)
const axios = securedAxios()

watch(voiceMailMessageType, (_value) => {
  if (!voiceMailText.value) {
    voiceMailText.value = props.defaultVoiceMailMessage
  }
})

watch(recordingEnabled, (value) => {
  if (!value) {
    recordingWarningType.value = 'default'
  }
})

watch(recordingWarningType, (value) => {
  if (value === 'default') {
    recordingWarningText.value = props.defaultRecordingWarning
  }
})

function collectParams() {
  return {
    phone_number: {
      call_flow_active: isActive.value,
      friendly_name: friendlyName.value,
      leg_length: legLength.value,
      caller_id_mode: callerIdMode.value,
      call_flow_type: callFlowType.value,
      challenge_enabled: challengeEnabled.value,
      roboblock_enabled: roboblockEnabled.value,
      blocklist_enabled: blocklistEnabled.value,
      ring_through_number: ringThroughNumber.value,
      time_zone: selectedTimeZone.value.id,
      schedule: snakecaseKeys(scheduleSettings.value, { deep: true }),
      voice_mail_message_type: voiceMailMessageType.value,
      voice_mail_text: voiceMailText.value,
      voice_mail_url: voiceMailUrl.value,
      vm_transcription: vmTranscription.value,
      direct_to_vm: directToVm.value,
      send_notification: sendNotification.value,
      recording_enabled: recordingEnabled.value,
      recording_warning_type: recordingWarningType.value,
      recording_warning_text: recordingWarningText.value,
      recording_warning_url: recordingWarningUrl.value
    }
  }
}

async function updateSettings() {
  if (errorText('ringThroughNumber')) {
    toastError('Unable to save changes due to one or several input fields being invalid. Please correct the errors and try again.')
    return
  }

  formSubmitting.value = true

  try {
    const params = collectParams()
    await axios.put(`/ajax/phone_numbers/${props.phoneNumber.id}`, params)
    toastSuccess('Phone number configured successfully!')
  } catch (error) {
    // errors should be an object like:
    //
    //   {
    //     "type": "validation",
    //     "text": "Phone number is invalid",
    //     "details": {
    //       "ring_through_number": ["can't be blank"]
    //     }
    //   }
    updateErrors(error.response.data)
    toastError(error)
  } finally {
    formSubmitting.value = false
  }
}

</script>
